import { useCallback, useEffect,useMemo, useState } from "react";
import validateInput from "@cores/validateInput";
import { LIMIT_CHAR } from "@constants";
import { AutocompleteTags } from "@components_v2/autocomplete";
import { useTranslation } from "react-i18next";
import i18n from "@lib/i18n";

const TagsInput = ({ tags, label, disabled = false, inputDisabled = false, onDeleteTag, onAddTag, isEdit, maxCount, isClear }) => {
    const { t } = useTranslation();
    const [value, setValue] = useState("");
    const [validationError, setValidationError] = useState(null);
    const MAX_COUNT = maxCount || 100;

    const validate = useCallback(
        (newValue, currentTags = tags) => {
            if (!newValue) return null;
            if (currentTags?.length >= MAX_COUNT)
                return {
                    level: "error",
                    message: i18n.t(`common::msg::The maximum number of tags, {{maxCount}}, has been exceeded.`, {
                        maxCount: MAX_COUNT,
                    }),
                };
            return validateInput(newValue, {
                maxLength: LIMIT_CHAR.tag,
                uniqueInTrimmed: currentTags?.map((tag) => tag.value) || [],
            });
        },
        [tags, MAX_COUNT],
    );

    const onHandleDeleteTag = useCallback(
        (index) => {
            const updatedTags = tags.filter((_, i) => i !== index);
            setValidationError(validate(value, updatedTags));
            onDeleteTag(index);
        },
        [onDeleteTag, tags, value, validate],
    );

    const handleSetValue = useCallback(
        (newValue) => {
            setValue(newValue);
            setValidationError(validate(newValue));
        },
        [validate],
    );

    const onHandleSubmitTag = useCallback(() => {
        // 태그는 앞뒤 공백만 제거해 등록한다 — 내부 공백은 값의 일부로 보존한다. 공백만 입력한 값은 등록하지 않는다.
        const trimmedValue = value.trim();
        if (!trimmedValue || validationError) return;
        onAddTag({ value: trimmedValue });
        setValue("");
        setValidationError(null);
    }, [value, onAddTag, validationError]);

    useEffect(() => {
        if (isClear) {
            setValue("");
            setValidationError(null);
        }
    }, [isClear]);

    return (
        <AutocompleteTags
            type={"tag"}
            label={label}
            value={value}
            placeholder={t(`common::msg::Enter after typing`)}
            tags={tags}
            disabled={disabled}
            inputDisabled={inputDisabled}
            setValue={handleSetValue}
            onDelete={onHandleDeleteTag}
            onSubmit={onHandleSubmitTag}
            isEdit={isEdit}
            maxCount={MAX_COUNT}
            helperText={validationError?.message || ""}
            error={validationError?.level === "error"}
        />
    );
};

export default TagsInput;
